import { type AdminUserCompanyTagDto, ImplicitTagSource } from '@@types/api.types';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useAdminParentOrganizationsQuery } from '@queries/admin-organizations.query';
import { useAdminGetTags, useAdminUpdateUserAccessControls } from '@queries/admin-users.query';
import { useStore } from '@tanstack/react-form';
import { hasArrayChanges } from '@utils/array.util';
import { IMPLICIT_TAG_OPTIONS, getImplicitTagSourceLabel, isAllTagName } from '@utils/tag.util';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { formatOrganizationShortId } from '../../organizations/organization-hierarchy.utils';

const MAX_ORGANIZATIONS_PER_LIST = 20;
const ALL_TAG_NAME = 'ALL';
const DELETED_ORGANIZATION_TOOLTIP = 'This organization is currently deleted.';
const IMPLICIT_TAG_OPTIONS_LIST = IMPLICIT_TAG_OPTIONS;
const ORGANIZATION_BLACKLIST_DISABLED_TOOLTIP =
  'Blacklist is available only when at least one organization tag is selected.';
const ORGANIZATION_BLACKLIST_TOOLTIP = 'Organizations will be removed from the tag-based list';
const ORGANIZATION_WHITELIST_TOOLTIP = 'Organizations will be added to the tag-based access list';
const ORGANIZATIONS_TAGS_TOOLTIP = 'This user can access organizations with these tags';

const accessControlsFormSchema = z.object({
  organizationBlacklistIds: z.array(z.string().min(1)).max(MAX_ORGANIZATIONS_PER_LIST),
  organizationWhitelistIds: z.array(z.string().min(1)).max(MAX_ORGANIZATIONS_PER_LIST),
  tagNames: z.array(z.string()),
});

type AccessControlsFormValues = z.infer<typeof accessControlsFormSchema>;

type OrganizationLookupOrganization = {
  deleted?: boolean;
  id: string;
  name: string;
};

type OrganizationSelectOption = {
  deleted?: boolean;
  description: string;
  disabled?: boolean;
  disabledTooltip?: string;
  label: string;
  value: string;
};

type UseAdminUserAccessControlsFormParams = {
  externalBusy: boolean;
  organizationBlacklist?: string[];
  organizationWhitelist?: string[];
  userId: string;
  userTags: AdminUserCompanyTagDto[];
};

export type AdminUserAccessControlsFieldName = 'organizationBlacklistIds' | 'organizationWhitelistIds';

export type AdminUserAccessControlsFieldConfig = {
  disabled?: boolean;
  emptyText: string;
  fieldName: AdminUserAccessControlsFieldName;
  organizationIds: string[];
  options: OrganizationSelectOption[];
  title: string;
  tooltip: string;
};

type SelectedImplicitTag = {
  label: string;
  tagName: string;
};

export type AdminUserAccessControlsResolvedOrganizationOption = {
  deleted?: boolean;
  disabledTooltip?: string;
  label: string;
};

const normalizeTagNames = (params: {
  implicitValueLookup: Map<string, string>;
  tagNames: string[];
}): string[] => {
  const { implicitValueLookup, tagNames } = params;
  const normalizedTagNames: string[] = [];
  const seen = new Set<string>();

  for (const tagName of tagNames) {
    const trimmedTagName = tagName.trim();
    if (!trimmedTagName) continue;

    const normalizedTagName =
      implicitValueLookup.get(trimmedTagName.toLowerCase()) ??
      (isAllTagName(trimmedTagName) ? ALL_TAG_NAME : trimmedTagName);
    const normalizedTagNameKey = normalizedTagName.toLowerCase();

    if (seen.has(normalizedTagNameKey)) continue;

    seen.add(normalizedTagNameKey);
    normalizedTagNames.push(normalizedTagName);
  }

  return normalizedTagNames;
};

const normalizeOrganizationIds = (organizationIds?: string[]): string[] => {
  const normalizedIds: string[] = [];
  const seen = new Set<string>();

  for (const organizationId of organizationIds ?? []) {
    const normalizedId = organizationId.trim().toLowerCase();
    if (!normalizedId) continue;
    if (seen.has(normalizedId)) continue;

    seen.add(normalizedId);
    normalizedIds.push(normalizedId);
  }

  return normalizedIds;
};

const normalizeStringArrayValue = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string');
};

const buildOrganizationOptions = (params: {
  organizationOptionById: Map<string, OrganizationSelectOption>;
  selectedOrganizationIds: string[];
}): OrganizationSelectOption[] => {
  const { organizationOptionById, selectedOrganizationIds } = params;
  const selectedOrganizationIdSet = new Set(selectedOrganizationIds);

  return [...organizationOptionById.values()].filter((organizationOption) => {
    if (!organizationOption.deleted) {
      return true;
    }

    return selectedOrganizationIdSet.has(organizationOption.value);
  });
};

const buildOrganizationOptionLookup = (params: {
  organizations: OrganizationLookupOrganization[];
}): Map<string, OrganizationSelectOption> => {
  const { organizations } = params;
  const lookup = new Map<string, OrganizationSelectOption>();

  for (const organization of organizations) {
    const normalizedOrganizationId = organization.id.toLowerCase();
    const isDeleted = organization.deleted === true;
    lookup.set(normalizedOrganizationId, {
      deleted: isDeleted,
      description: `ID: ${formatOrganizationShortId(organization.id)}`,
      disabled: isDeleted,
      disabledTooltip: isDeleted ? DELETED_ORGANIZATION_TOOLTIP : undefined,
      label: organization.name,
      value: normalizedOrganizationId,
    });
  }

  return lookup;
};

export const useAdminUserAccessControlsForm = (params: UseAdminUserAccessControlsFormParams) => {
  const { externalBusy, organizationBlacklist, organizationWhitelist, userId, userTags } = params;

  const { data: availableTags = [] } = useAdminGetTags();
  const { data: allParentOrganizationsData } = useAdminParentOrganizationsQuery({ includeDeleted: true });
  const { isPending: isUpdating, mutateAsync: updateUserAccessControls } = useAdminUpdateUserAccessControls();

  const [createdTagNames, setCreatedTagNames] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);

  const implicitValueLookup = useMemo(() => {
    const lookup = new Map<string, string>();

    lookup.set(ALL_TAG_NAME.toLowerCase(), ALL_TAG_NAME);
    for (const implicitTagOption of IMPLICIT_TAG_OPTIONS_LIST) {
      lookup.set(implicitTagOption.label.toLowerCase(), implicitTagOption.value);
      lookup.set(implicitTagOption.value.toLowerCase(), implicitTagOption.value);
    }

    return lookup;
  }, []);

  const initialTagNames = useMemo(() => {
    return normalizeTagNames({
      implicitValueLookup,
      tagNames: userTags.map((tag) => tag.name),
    });
  }, [implicitValueLookup, userTags]);
  const initialOrganizationBlacklistIds = useMemo(() => {
    return normalizeOrganizationIds(organizationBlacklist);
  }, [organizationBlacklist]);
  const initialOrganizationWhitelistIds = useMemo(() => {
    return normalizeOrganizationIds(organizationWhitelist);
  }, [organizationWhitelist]);
  const initialFormValues = useMemo<AccessControlsFormValues>(() => {
    return {
      organizationBlacklistIds: initialOrganizationBlacklistIds,
      organizationWhitelistIds: initialOrganizationWhitelistIds,
      tagNames: initialTagNames,
    };
  }, [initialOrganizationBlacklistIds, initialOrganizationWhitelistIds, initialTagNames]);

  const form = useAppForm({
    defaultValues: initialFormValues,
    onSubmit: async ({ value }) => {
      await updateUserAccessControls({
        id: userId,
        organizationBlacklist: value.organizationBlacklistIds,
        organizationWhitelist: value.organizationWhitelistIds,
        tags: value.tagNames,
      });

      setCreatedTagNames([]);
      setIsEditing(false);
      successToast({ description: 'Access controls updated successfully.' });
    },
    validators: {
      onSubmit: accessControlsFormSchema,
    },
  });

  const selectedOrganizationBlacklistIds = useStore(form.store, (state) => state.values.organizationBlacklistIds);
  const selectedOrganizationWhitelistIds = useStore(form.store, (state) => state.values.organizationWhitelistIds);
  const selectedTagNames = useStore(form.store, (state) => state.values.tagNames);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const hasSelectedOrganizationTags = selectedTagNames.length > 0;
  const { data: filteredParentOrganizationsData } = useAdminParentOrganizationsQuery({
    includeDeleted: true,
    tagNames: hasSelectedOrganizationTags ? selectedTagNames : undefined,
  });

  const isBusy = externalBusy || isSubmitting || isUpdating;

  const implicitTagMap = useMemo(() => {
    return new Map(IMPLICIT_TAG_OPTIONS_LIST.map((option) => [option.value.toLowerCase(), option]));
  }, []);

  const parentOrganizations = useMemo(() => {
    return allParentOrganizationsData?.organizations ?? [];
  }, [allParentOrganizationsData]);
  const filteredParentOrganizations = useMemo(() => {
    if (!hasSelectedOrganizationTags) {
      return [];
    }

    return filteredParentOrganizationsData?.organizations ?? [];
  }, [filteredParentOrganizationsData, hasSelectedOrganizationTags]);
  const organizationOptionById = useMemo(() => {
    return buildOrganizationOptionLookup({
      organizations: parentOrganizations,
    });
  }, [parentOrganizations]);
  const filteredOrganizationOptionById = useMemo(() => {
    return buildOrganizationOptionLookup({
      organizations: filteredParentOrganizations,
    });
  }, [filteredParentOrganizations]);

  const whitelistOrganizationOptions = useMemo(() => {
    return buildOrganizationOptions({
      organizationOptionById,
      selectedOrganizationIds: [...initialOrganizationWhitelistIds, ...selectedOrganizationWhitelistIds],
    });
  }, [initialOrganizationWhitelistIds, organizationOptionById, selectedOrganizationWhitelistIds]);
  const blacklistOrganizationOptions = useMemo(() => {
    return buildOrganizationOptions({
      organizationOptionById: hasSelectedOrganizationTags ? filteredOrganizationOptionById : new Map(),
      selectedOrganizationIds: [...initialOrganizationBlacklistIds, ...selectedOrganizationBlacklistIds],
    });
  }, [
    filteredOrganizationOptionById,
    hasSelectedOrganizationTags,
    initialOrganizationBlacklistIds,
    selectedOrganizationBlacklistIds,
  ]);
  const allowedBlacklistOrganizationIdSet = useMemo(() => {
    return new Set(filteredOrganizationOptionById.keys());
  }, [filteredOrganizationOptionById]);
  const organizationAccessFieldConfigs = useMemo<AdminUserAccessControlsFieldConfig[]>(() => {
    return [
      {
        emptyText: 'No organizations whitelisted',
        fieldName: 'organizationWhitelistIds',
        organizationIds: selectedOrganizationWhitelistIds,
        options: whitelistOrganizationOptions,
        title: 'Organization Whitelist',
        tooltip: ORGANIZATION_WHITELIST_TOOLTIP,
      },
      {
        disabled: !hasSelectedOrganizationTags,
        emptyText: 'No organizations blacklisted',
        fieldName: 'organizationBlacklistIds',
        organizationIds: selectedOrganizationBlacklistIds,
        options: blacklistOrganizationOptions,
        title: 'Organization Blacklist',
        tooltip: hasSelectedOrganizationTags ? ORGANIZATION_BLACKLIST_TOOLTIP : ORGANIZATION_BLACKLIST_DISABLED_TOOLTIP,
      },
    ];
  }, [
    blacklistOrganizationOptions,
    hasSelectedOrganizationTags,
    selectedOrganizationBlacklistIds,
    selectedOrganizationWhitelistIds,
    whitelistOrganizationOptions,
  ]);

  const resolveOrganizationOption = useCallback(
    (organizationId: string): AdminUserAccessControlsResolvedOrganizationOption | null => {
      const option = organizationOptionById.get(organizationId.toLowerCase());
      if (option) {
        return {
          deleted: option.deleted,
          disabledTooltip: option.disabledTooltip,
          label: option.label,
        };
      }

      return null;
    },
    [organizationOptionById],
  );

  const resolveTagName = useCallback(
    (tagName: string): string => {
      const trimmedTagName = tagName.trim();
      if (!trimmedTagName) return '';

      return implicitValueLookup.get(trimmedTagName.toLowerCase()) ?? trimmedTagName;
    },
    [implicitValueLookup],
  );

  const allCustomTagNames = useMemo(() => {
    const allTagNames = [...availableTags.map((tag) => tag.name), ...createdTagNames];
    return normalizeTagNames({
      implicitValueLookup,
      tagNames: allTagNames,
    }).filter((tagName) => !isAllTagName(tagName) && !implicitTagMap.has(tagName.toLowerCase()));
  }, [availableTags, createdTagNames, implicitTagMap, implicitValueLookup]);
  const allSelectableTagOptions = useMemo(() => {
    const implicitOptions = [
      {
        description: getImplicitTagSourceLabel(ImplicitTagSource.SPECIAL),
        label: ALL_TAG_NAME,
        value: ALL_TAG_NAME,
      },
      ...IMPLICIT_TAG_OPTIONS_LIST.map((option) => ({
        description: getImplicitTagSourceLabel(option.source),
        label: option.value,
        value: option.value,
      })),
    ];

    const customOptions = allCustomTagNames.map((tagName) => ({
      description: 'Custom',
      label: tagName,
      value: tagName,
    }));

    return [...implicitOptions, ...customOptions];
  }, [allCustomTagNames]);

  const selectedImplicitTags = useMemo(() => {
    return selectedTagNames
      .filter((tagName) => isAllTagName(tagName) || implicitTagMap.has(tagName.toLowerCase()))
      .map((tagName) => {
        const implicitOption = implicitTagMap.get(tagName.toLowerCase());
        const sourceLabel = implicitOption ? getImplicitTagSourceLabel(implicitOption.source) : 'Implicit';

        return {
          label: isAllTagName(tagName) ? tagName : `${sourceLabel}: ${tagName}`,
          tagName,
        };
      });
  }, [implicitTagMap, selectedTagNames]);
  const selectedCustomTags = useMemo(() => {
    return selectedTagNames.filter((tagName) => !isAllTagName(tagName) && !implicitTagMap.has(tagName.toLowerCase()));
  }, [implicitTagMap, selectedTagNames]);
  const isImplicitOrAllTag = useCallback(
    (tagName: string): boolean => {
      return isAllTagName(tagName) || implicitTagMap.has(tagName.toLowerCase());
    },
    [implicitTagMap],
  );

  const hasChanges = useMemo(() => {
    return (
      hasArrayChanges({ next: selectedTagNames, previous: initialTagNames }) ||
      hasArrayChanges({ next: selectedOrganizationBlacklistIds, previous: initialOrganizationBlacklistIds }) ||
      hasArrayChanges({ next: selectedOrganizationWhitelistIds, previous: initialOrganizationWhitelistIds })
    );
  }, [
    initialOrganizationBlacklistIds,
    initialOrganizationWhitelistIds,
    initialTagNames,
    selectedOrganizationBlacklistIds,
    selectedOrganizationWhitelistIds,
    selectedTagNames,
  ]);

  const handleOrganizationAccessFieldChange = useCallback(
    (params: {
      fieldName: AdminUserAccessControlsFieldName;
      value: unknown;
    }): void => {
      const { fieldName, value } = params;
      if (!isEditing) {
        return;
      }

      const previousOrganizationBlacklistIds = normalizeOrganizationIds(
        form.getFieldValue('organizationBlacklistIds') ?? [],
      );
      const previousOrganizationWhitelistIds = normalizeOrganizationIds(
        form.getFieldValue('organizationWhitelistIds') ?? [],
      );

      let nextOrganizationBlacklistIds =
        fieldName === 'organizationBlacklistIds'
          ? normalizeOrganizationIds(normalizeStringArrayValue(value))
          : previousOrganizationBlacklistIds;
      let nextOrganizationWhitelistIds =
        fieldName === 'organizationWhitelistIds'
          ? normalizeOrganizationIds(normalizeStringArrayValue(value))
          : previousOrganizationWhitelistIds;

      if (fieldName === 'organizationBlacklistIds') {
        if (!hasSelectedOrganizationTags) {
          nextOrganizationBlacklistIds = [];
        }

        if (nextOrganizationBlacklistIds.length > MAX_ORGANIZATIONS_PER_LIST) {
          form.setFieldValue('organizationBlacklistIds', previousOrganizationBlacklistIds);
          errorToast({ description: `Blacklist can include up to ${MAX_ORGANIZATIONS_PER_LIST} organizations.` });
          return;
        }

        const organizationBlacklistIdSet = new Set(nextOrganizationBlacklistIds);
        nextOrganizationWhitelistIds = nextOrganizationWhitelistIds.filter(
          (organizationId) => !organizationBlacklistIdSet.has(organizationId),
        );
      }

      if (fieldName === 'organizationWhitelistIds') {
        if (nextOrganizationWhitelistIds.length > MAX_ORGANIZATIONS_PER_LIST) {
          form.setFieldValue('organizationWhitelistIds', previousOrganizationWhitelistIds);
          errorToast({ description: `Whitelist can include up to ${MAX_ORGANIZATIONS_PER_LIST} organizations.` });
          return;
        }

        const organizationWhitelistIdSet = new Set(nextOrganizationWhitelistIds);
        nextOrganizationBlacklistIds = nextOrganizationBlacklistIds.filter(
          (organizationId) => !organizationWhitelistIdSet.has(organizationId),
        );
      }

      if (hasArrayChanges({ next: nextOrganizationBlacklistIds, previous: previousOrganizationBlacklistIds })) {
        form.setFieldValue('organizationBlacklistIds', nextOrganizationBlacklistIds);
      }

      if (hasArrayChanges({ next: nextOrganizationWhitelistIds, previous: previousOrganizationWhitelistIds })) {
        form.setFieldValue('organizationWhitelistIds', nextOrganizationWhitelistIds);
      }
    },
    [form, hasSelectedOrganizationTags, isEditing],
  );

  const handleTagNamesFieldChange = useCallback(
    (value: unknown): void => {
      if (!isEditing) {
        return;
      }

      const rawTagNames = normalizeStringArrayValue(value);
      const nextTagNames = normalizeTagNames({
        implicitValueLookup,
        tagNames: rawTagNames,
      });

      if (hasArrayChanges({ next: nextTagNames, previous: rawTagNames })) {
        form.setFieldValue('tagNames', nextTagNames);
      }

      if (nextTagNames.length > 0) {
        return;
      }

      if (selectedOrganizationBlacklistIds.length > 0) {
        form.setFieldValue('organizationBlacklistIds', []);
      }
    },
    [form, implicitValueLookup, isEditing, selectedOrganizationBlacklistIds],
  );

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    if (!allParentOrganizationsData) {
      return;
    }

    const filteredOrganizationWhitelistIds = selectedOrganizationWhitelistIds.filter((organizationId) =>
      organizationOptionById.has(organizationId),
    );
    if (
      hasArrayChanges({
        next: filteredOrganizationWhitelistIds,
        previous: selectedOrganizationWhitelistIds,
      })
    ) {
      form.setFieldValue('organizationWhitelistIds', filteredOrganizationWhitelistIds);
    }

    const filteredOrganizationBlacklistIds = selectedOrganizationBlacklistIds.filter((organizationId) =>
      organizationOptionById.has(organizationId),
    );
    let nextOrganizationBlacklistIds = filteredOrganizationBlacklistIds;

    if (!hasSelectedOrganizationTags) {
      nextOrganizationBlacklistIds = [];
    } else if (filteredParentOrganizationsData) {
      nextOrganizationBlacklistIds = filteredOrganizationBlacklistIds.filter((organizationId) =>
        allowedBlacklistOrganizationIdSet.has(organizationId),
      );
    }

    if (
      hasArrayChanges({
        next: nextOrganizationBlacklistIds,
        previous: selectedOrganizationBlacklistIds,
      })
    ) {
      form.setFieldValue('organizationBlacklistIds', nextOrganizationBlacklistIds);
    }
  }, [
    allParentOrganizationsData,
    allowedBlacklistOrganizationIdSet,
    filteredParentOrganizationsData,
    form,
    hasSelectedOrganizationTags,
    isEditing,
    organizationOptionById,
    selectedOrganizationBlacklistIds,
    selectedOrganizationWhitelistIds,
  ]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!hasChanges) {
      setCreatedTagNames([]);
      setIsEditing(false);
      return;
    }

    try {
      await form.handleSubmit();
    } catch {
      errorToast({ description: 'Failed to update access controls.' });
    }
  }, [form, hasChanges]);

  const handleCancel = useCallback((): void => {
    form.reset(initialFormValues);
    setCreatedTagNames([]);
    setIsEditing(false);
  }, [form, initialFormValues]);

  const handleEdit = useCallback((): void => {
    setIsEditing(true);
  }, []);

  const handleCreateTagOption = useCallback(
    (tagName: string): void => {
      const normalizedTagName = resolveTagName(tagName);
      if (!normalizedTagName) return;

      form.setFieldValue('tagNames', (currentTagNames) =>
        normalizeTagNames({
          implicitValueLookup,
          tagNames: [...(currentTagNames ?? []), normalizedTagName],
        }),
      );

      if (isAllTagName(normalizedTagName) || implicitTagMap.has(normalizedTagName.toLowerCase())) {
        return;
      }

      setCreatedTagNames((currentTagNames) =>
        normalizeTagNames({
          implicitValueLookup,
          tagNames: [...currentTagNames, normalizedTagName],
        }),
      );
    },
    [form, implicitTagMap, implicitValueLookup, resolveTagName],
  );

  return {
    allSelectableTagOptions,
    form,
    handleCancel,
    handleCreateTagOption,
    handleEdit,
    handleOrganizationAccessFieldChange,
    handleSave,
    handleTagNamesFieldChange,
    isBusy,
    isEditing,
    isImplicitOrAllTag,
    maxOrganizationsPerList: MAX_ORGANIZATIONS_PER_LIST,
    organizationAccessFieldConfigs,
    organizationBlacklistDisabledTooltip: ORGANIZATION_BLACKLIST_DISABLED_TOOLTIP,
    organizationsTagsTooltip: ORGANIZATIONS_TAGS_TOOLTIP,
    resolveOrganizationOption,
    selectedCustomTags,
    selectedImplicitTags,
    selectedTagNames,
  };
};
